import React, { useRef, useState, useEffect  }  from "react";
import { ItemList } from "./ItemList";
import { extractSuffixFromField, sendRequest } from "./vmUtils";
import { lambdaURL } from "../config";
import { useAuth } from './vmAuthenticate';

// useVMObject.js - Custom hook for shared state and behavior
const useVMObject = (objectType, refreshTrigger, options) => {
  const [objects, setObjects] = useState([]);
  const [objectUuid, setObjectUuid] = useState('');
  const [objectOwnerUuid, setObjectOwnerUuid] = useState('');
  const [objectTitle, setObjectTitle] = useState('');
  const [objectDescription, setObjectDescription] = useState('');
  const [showObjectForm, setShowObjectForm] = useState(false);
  const [isNewObject, setIsNewObject] = useState(false);
  const [isObjectOwner, setIsObjectOwner] = useState(false);
  const [isListDirty, setIsListDirty] = useState(false);
  const { user } = useAuth();
  const userId = user?.userId;
  //console.log("useVMObject hook called with options: ", options);

  // Configuration object for different types
  const config = {
    assembly: {
      commands: {
        getAll: "getUserAssemblies",
        create: "createAssembly",
        update: "updateAssembly",
        delete: "deleteAssembly"
      },
      titleField: "name",
      idField: "uuid"
    },
    proposal: {
      commands: {
        getAll: "getAllProposals",
        create: "createProposal",
        update: "updateProposal",
        delete: "deleteProposal"
      },
      titleField: "title",
      idField: "uuid"
    },
    user: {
      commands: {
        getAll: "getAllUsers",
        create: "createUser",
        update: "updateUser",
        delete: "deleteUser"
      },
      titleField: "name",
      idField: "uuid"
    },
    topic: {
      commands: {
        getAll: "getAllTopics",
        create: "createTopic",
        update: "updateTopic",
        delete: "deleteTopic"
      },
      titleField: "name",
      idField: "uuid"
    },
    source: {
      commands: {
        getAll: "getAllSources",
        create: "createSource",
        update: "updateSource",
        delete: "deleteSource"
      },
      titleField: "name",
      idField: "uuid"
    }
  };

  const currentConfig = config[objectType];

  useEffect(() => {
    if (!userId) {
      console.log("No userId yet, waiting...");
      return;
    }

    let command = currentConfig.commands.getAll;
    let jsonData = {
      command,
      userId: userId
    };

    // Special handling for proposals within an assembly
    if (options && objectType === 'proposal' && options?.assemblyId) {
      console.log("Fetching proposals for assembly:", options.assemblyId);
      jsonData = {
        command: 'getAssemblyProposals',
        assemblyId: options.assemblyId,
        topicId: options.topicId,
        limit: options.limit || 50,
        descending: options.sortDescending !== false
      };
    } //else {
      //console.log("something didn't work, options: ", options);
    //}

    console.log("Fetching objects for type:", objectType, jsonData);
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        const parsedBody = JSON.parse(data.body);
        if (parsedBody && Array.isArray(parsedBody)) {
          setObjects(parsedBody);
        }
      }
    });
  }, [isListDirty, showObjectForm, currentConfig.commands.getAll, userId, refreshTrigger]);

  const handleOpenObjectForm = () => {
    setIsNewObject(true);
    setShowObjectForm(true);
    setIsObjectOwner(true);  // Important: user is owner of new objects
    setObjectTitle('');      // Clear form
    setObjectDescription('');
  };

  const handleCloseObjectForm = () => {
    setShowObjectForm(false);
    clearObjectForm();
  };

  const clearObjectForm = () => {
    setObjectUuid(null);
    setObjectOwnerUuid(null);
    setObjectTitle('');
    setObjectDescription('');
  };

  const handleCreateObject = () => {
    if (!isNewObject) return;
    
    let jsonData = {
      command: currentConfig.commands.create,
      [currentConfig.titleField]: objectTitle,
      description: objectDescription
    };
    
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        setShowObjectForm(false);
        clearObjectForm();
        setIsListDirty(true);
      }
    })
  };

  const handleUpdateObject = () => {
    let jsonData = {
      command: currentConfig.commands.update,
      uuid: objectUuid,
      [currentConfig.titleField]: objectTitle,
      description: objectDescription
    };
    
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        setShowObjectForm(false);
        clearObjectForm();
        setIsListDirty(true);
      }
    });
  };

  const handleDeleteObject = () => {
    let jsonData = {
      command: currentConfig.commands.delete,
      uuid: objectUuid
    };
    
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        setShowObjectForm(false);
        clearObjectForm();
        setIsListDirty(true);
      }
    });
  };

  const handleObjectClick = (object) => {
    console.log('Object clicked:', object);
    const objectUserId = extractUserIdFromPK(object.PK);
    console.log('Extracted userId:', objectUserId);
    console.log('Current user UUID:', userId);
    
    setObjectUuid(object[currentConfig.idField]);
    setObjectOwnerUuid(objectUserId);
    setObjectTitle(object[currentConfig.titleField]);
    setObjectDescription(object.description);
    setIsNewObject(false);
    setShowObjectForm(true);
    setIsObjectOwner(objectUserId === userId);
    
    const mainPanel = document.getElementById('vm-clipper');
    if (mainPanel) {
      mainPanel.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const fetchAllAccessibleAssemblies = () => {
    //console.log("Fetching all accessible assemblies, user: ", user_uuid);
    let jsonData = {
      command: "getAllAccessibleAssemblies"
    };
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        const parsedBody = JSON.parse(data.body);
        if (parsedBody && Array.isArray(parsedBody)) {
          setObjects(parsedBody);
        }
      }
    });
  };

  return {
    objects,
    objectTitle,
    objectDescription,
    showObjectForm,
    isNewObject,
    isObjectOwner,
    handleOpenObjectForm,
    handleCloseObjectForm,
    handleCreateObject,
    handleUpdateObject,
    handleDeleteObject,
    handleObjectClick,
    setObjectTitle,
    setObjectDescription,
    setIsListDirty,
    currentConfig
  };
};


//////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////


const VMObjectComponent = ({
  objectType,
  user_uuid,
  title,
  title_singular,
  onCustomClick,
  onEditClick,
  renderItem,
  concise = false,
  refreshTrigger = 0,
  options = {}
}) => {
  //console.log("VMObjectComponent received props:", { objectType, title, options });
  const {
    objects,
    objectTitle,
    objectDescription,
    showObjectForm,
    isNewObject,
    isObjectOwner,
    handleOpenObjectForm,
    handleCloseObjectForm,
    handleCreateObject,
    handleUpdateObject,
    handleDeleteObject,
    handleObjectClick,
    setObjectTitle,
    setObjectDescription,
    setIsListDirty,
    currentConfig
  } = useVMObject(objectType, refreshTrigger, options);

  const [editMode, setEditMode] = useState(false);

  const handleManageClick = (item, e) => {
    e.stopPropagation();
    // If we have an onEditClick handler, use that instead of the default form behavior
    if (onEditClick) {
      onEditClick(item);
    } else {
      // Fall back to original behavior
      setEditMode(true);
      handleObjectClick(item);
    }
  };

  const handleSaveClick = () => {
    handleUpdateObject();
    setEditMode(false);
    handleCloseObjectForm();
  };

  const handleCancelClick = () => {
    setEditMode(false);
    handleCloseObjectForm();
  };

  const onMoveItem = async (index, direction) => {
    const items = [...objects];
    const currentItem = items[index];
    const otherIndex = direction === 'up' ? index - 1 : index + 1;
    const otherItem = items[otherIndex];

    console.log("Moving item!! index ", index, " direction ", direction);

    // Don't allow moving if either item is Vote Machine
    if (currentItem.sort === 999999 || otherItem?.sort === 999999) {
      return;
    }

    // Swap sort values
    const tempSort = currentItem.sort;
    currentItem.sort = otherItem.sort;
    otherItem.sort = tempSort;

    // Update in database
    try {
      const jsonData = {
        command: "updateAssemblyOrder",
        assemblies: items
      };
      console.log("sending updateAssemblyOrder request: " + JSON.stringify(jsonData));
      const response = await sendRequest(lambdaURL, jsonData);
      console.log("updateAssemblyOrder response: " + JSON.stringify(response));
      setIsListDirty(prev => !prev); // Toggle instead of just setting to true
      if (refreshTrigger) {
        refreshTrigger(prev => prev + 1); // Increment refresh trigger if available
      }
    } catch (error) {
      console.error('Error updating assembly order:', error);
    }
  };

  const renderItemWithManage = (item, index, total) => {
    const itemUserId = extractUserIdFromPK(item.PK);
    const isOwner = (itemUserId === user_uuid);

    //${index === 0 ? 'text-gray-300' : 'text-gray-600'}
    //${index === total - 1 ? 'text-gray-300' : 'text-gray-600'}

    return (
      <div className="vm-item-container">
        {objectType === "assembly" && item.sort !== 999999 && item.sort !== 0 && (
          <div className="flex flex-col" style={{ marginRight: '16px', width: '5%' }}>
            { item.sort !== 1 && (
              <button
                className={`p-1 rounded font-bold hover:bg-gray-100 `}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log("trying onClick, index: " + index + "  total " + total);
                  if (index > 1) onMoveItem(index, 'up');
                }}
                disabled={index === 0}
                >
                <span style={{ width: '200px' }}>⬆</span>
              </button>
            )}
            <br></br>
            { item.sort !== (total - 2) && (
              <button
                className={`p-1 mt-8 rounded font-bold hover:bg-gray-100 `}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log("trying onClick, index: " + index + "  total " + total);
                  if (index < total - 2) onMoveItem(index, 'down');
                }}
                disabled={index === total - 1}
              >
                <span style={{  width: '200px' }}>⬇</span>
              </button>
            )}
          </div>
        )}
        <div className="vm-item-content" style={{ marginRight: '16px', width: '95%' }}>
          {renderItem ? 
            renderItem(item) : 
            <>
              <div className="vm-item-title">{item[currentConfig.titleField]}</div>
              {item.description && (
                <div
                  className="vm-item-description"
                  style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    WebkitLineClamp: 2,
                  }}
                >
                  {item.description}
                </div>
              )}
              <div className="text-sm text-gray-600" style={{ fontSize: '12px'}}>
                Author: {itemUserId}
              </div>
            </>
          }
        </div>
        
        {isOwner && (
          <button 
            className="vm-base-shadow-button-14"
            onClick={(e) => handleManageClick(item, e)}
          >
            Manage
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={concise ? "vm-section-concise" : "vm-section"}>
      {!concise && (
        <div className="vm-section-title"><h2>{title}</h2></div>
      )}
      {!showObjectForm && !concise && (
        <button 
          className="vm-form-button"
          onClick={handleOpenObjectForm}
        >
          Create {title_singular}
        </button>
      )}
      {showObjectForm && (
        <>
          
        <div className="vm-form-vertical">
            {(isNewObject || isObjectOwner) && (
            <div>
              <input
                className="vm-form-input"
                type="text"
                value={objectTitle || ''}
                onChange={(e) => setObjectTitle(e.target.value)}
                placeholder={`${title_singular} ${currentConfig.titleField}`}
              />
              <textarea
                className="vm-form-textarea"
                value={objectDescription || ''}
                onChange={(e) => setObjectDescription(e.target.value)}
                placeholder={`${title_singular} description`}
                rows="4"
              />
              <div className="vm-proposal-vote-button-container">
                {isNewObject ? (
                  <button 
                    className="vm-form-button"
                    onClick={handleCreateObject}
                  >
                    Add
                  </button>
                ) : (
                  <>
                    <button 
                      className="vm-form-button"
                      onClick={handleSaveClick}
                    >
                      Save
                    </button>
                    <button 
                      className="vm-form-button"
                      onClick={handleCancelClick}
                    >
                      Cancel
                    </button>
                    <button 
                      className="vm-form-button"
                      onClick={handleDeleteObject}
                    >
                      Deleeete
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
            {!isNewObject && !isObjectOwner && (
            <div>
              <div className="vm-form-text"><b>{objectTitle}</b></div>
              <br />
              <div className="vm-form-text">{objectDescription}</div>
            </div>
          )}
        </div>
        </>
      )}
      <div>
        <ItemList 
          items={objects} 
          onItemClick={onCustomClick || handleObjectClick}
          titleField={currentConfig.titleField}
          descriptionField="description"
          idField={currentConfig.idField}
          emptyMessage={`No ${title.toLowerCase()} found`}
          renderItem={renderItemWithManage}
        />
      </div>
    </div>
  );
};


//////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////


// Simplified component implementations
function VMAssembly({
  onAssemblyClick,
  onEditAssembly,
  interactive,
  renderItem,
  concise = false,
  refreshTrigger
}) {
  const { user } = useAuth();
  const userId = user?.userId;
  //const userId = localStorage.getItem('systemUserId');
  console.log("VMAssembly component loaded with userId:", userId);

  useEffect(() => {
    console.log("VMAssembly refreshTrigger changed to:", refreshTrigger);
  }, [refreshTrigger]);

  const {
    handleObjectClick: defaultObjectClick
  } = useVMObject("assembly", userId, refreshTrigger);

  const handleCustomClick = (assembly) => {
    if (interactive && onAssemblyClick) {
      console.log("onAssemblyClick:", assembly);
      onAssemblyClick(assembly);
    } else {
      console.log("Default object click:", assembly);
      defaultObjectClick(assembly);
    }
  };

  return <VMObjectComponent 
    objectType="assembly"
    user_uuid={userId}
    title="Assemblies"
    title_singular="Assembly"
    onCustomClick={handleCustomClick}
    onEditClick={onEditAssembly}
    renderItem={renderItem} 
    concise={concise}
    refreshTrigger={refreshTrigger}
  />;
}

function VMProposal({
  onProposalClick,
  onEditProposal,
  interactive = true,
  renderItem,
  concise = false,
  refreshTrigger = 0,
  options = {}
}) {
  const { user } = useAuth();
  const userId = user?.userId;

  const handleCustomClick = (proposal) => {
    if (interactive && onProposalClick) {
      onProposalClick(proposal);
    }
  };

  return <VMObjectComponent 
    objectType="proposal"
    user_uuid={userId}
    title="Proposals"
    title_singular="Proposal"
    onCustomClick={handleCustomClick}
    onEditClick={onEditProposal}
    renderItem={renderItem}
    concise={concise}
    refreshTrigger={refreshTrigger}
    options={options}
  />;
}

function VMUser({ user_uuid, renderItem, concise = false }) {
  return <VMObjectComponent 
    objectType="user"
    user_uuid={user_uuid}
    title="Voters"
    title_singular="Voter"
    renderItem={renderItem} 
    concise={concise}
  />;
}

function VMTopic({ user_uuid, renderItem, concise = false }) {
  return <VMObjectComponent 
    objectType="topic"
    user_uuid={user_uuid}
    title="Topics"
    title_singular="Topic"
    renderItem={renderItem} 
    concise={concise}
  />;
}

function VMSource({ user_uuid, renderItem, concise = false }) {
  return <VMObjectComponent 
    objectType="source"
    user_uuid={user_uuid}
    title="Sources"
    title_singular="Source"
    renderItem={renderItem} 
    concise={concise}
  />;
}


//////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////


const VMAssemblyMembership = ({ user_uuid, assembly_uuid }) => {
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // First get user's own membership status
    let jsonData = {
      command: "getMembershipStatus",
      assemblyId: assembly_uuid
    };
    
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        const parsedBody = JSON.parse(data.body);
        if (parsedBody && Array.isArray(parsedBody)) {
          setMembershipStatus(parsedBody.status);
          setIsAdmin(parsedBody.role === 'ADMIN');
          setIsLoading(false);
          if (parsedBody.role === 'ADMIN') {
            fetchPendingRequests();
          }
        }
      }
    });
  }, [user_uuid, assembly_uuid]);


  const fetchPendingRequests = () => {
    console.log('Fetching pending requests');
    let jsonData = {
      command: "getPendingAssemblyRequests",
      assemblyId: assembly_uuid
    };
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        const parsedBody = JSON.parse(data.body);
        if (parsedBody && Array.isArray(parsedBody)) {
          setPendingRequests(parsedBody || []);
        }
      }
    });
  };

  const requestMembership = () => {
    let jsonData = {
      command: "requestMembership",
      assemblyId: assembly_uuid
    };
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        setMembershipStatus('PENDING');
      }
    });
  };

  const handleRequest = (requestUserId, action) => {
    let jsonData = {
      command: "handleMembershipRequest",
      adminId: user_uuid,
      requestUserId: requestUserId,
      assemblyId: assembly_uuid,
      action: action
    };
    sendRequest(lambdaURL, jsonData)
    .then(data => {
      if (data.body) {
        setPendingRequests(prevRequests => 
          prevRequests.filter(req => req.userId !== requestUserId)
        );
      }
    });
  };

  const renderRequestItem = (request) => (
    <div className="vm-form-vertical">
      <div className="vm-form-text">Name: <b>{request.userName}</b></div>
      <div className="vm-form-text">Email: <b>{request.userEmail}</b></div>
      <div className="vm-proposal-vote-button-container">
        <button 
          className="vm-form-button"
          onClick={() => handleRequest(request.userId, 'APPROVE')}
        >
          Approve
        </button>
        <button 
          className="vm-form-button"
          onClick={() => handleRequest(request.userId, 'REJECT')}
        >
          Reject
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return <div className="vm-form-text">Loading...</div>;
  }

  return (
    <div className="vm-section">
      <div className="vm-proposals-header"><b>{assembly_uuid}</b></div>
      {!isAdmin && (
        <div className="vm-form-vertical">
          {membershipStatus === null && (
            <button 
              className="vm-form-button"
              onClick= {() => requestMembership()}
            >
              Request to Join
            </button>
          )}
          {membershipStatus === 'PENDING' && (
            <div className="vm-form-text">Request Pending</div>
          )}
          {membershipStatus === 'APPROVED' && (
            <div className="vm-form-text">Member</div>
          )}
          {membershipStatus === 'REJECTED' && (
            <div className="vm-form-text">Membership Denied</div>
          )}
        </div>
      )}

      {isAdmin && pendingRequests.length > 0 && (
        <div className="vm-form-vertical">
          <div className="vm-section-title">Pending Requests</div>
          <ItemList 
            items={pendingRequests}
            onItemClick={() => {}}
            titleField="userName"
            descriptionField="requestDate"
            idField="userId"
            emptyMessage="No pending requests"
            renderItem={renderRequestItem}
          />
        </div>
      )}
    </div>
  );
};

//////////////////////////////////////////////////////////////////////////////////////////////////

export const extractUserIdFromPK = (pk) => {
  return pk ? pk.replace('USER#', '') : null;
};

///////////////////////////////////////////////////////////////////////////////////////////////////////

export { 
  VMAssembly, 
  VMProposal,
  VMUser,
  VMTopic,
  VMSource,
  VMAssemblyMembership
};
